import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SUPPRESS_ERROR_NOTIFICATION } from '../api/interceptors/error.interceptor';
import { IMPERSONATION_SESSION_HEADER } from '../api/interceptors/header-injection.context';
import { AuthSessionStatus, AuthStateService } from '../auth';
import { ImpersonationService } from '../services/impersonation.service';
import { LookupService } from './lookup.service';
import { LookupBackgroundSyncProgress, LookupBackgroundSyncResult, LookupSyncTableProgress } from './lookup.types';

export type LookupBackgroundState = 'idle' | 'checking' | 'syncing' | 'completed' | 'failed';

export interface LookupBackgroundSyncConfig {
  enabled?: boolean;
  useServiceWorker?: boolean;
  enabledModules?: string[];
  minIntervalMs?: number;
  requestTimeoutMs?: number;
  tableFetchConcurrency?: number;
  autoStartOnStartup?: boolean;
  autoStartOnOnline?: boolean;
  autoStartOnVisible?: boolean;
  /** When true, app start loads every registered lookup table (not just the current module's) in the background. */
  loadFullDataOnStart?: boolean;
  /** Age (in days) of the oldest synced table before a full-data-on-start run forces a hard reload. */
  fullDataMaxAgeDays?: number;
}

export interface LookupSyncModuleView {
  state: LookupBackgroundState;
  progress: LookupBackgroundSyncProgress;
  lastRun: LookupBackgroundSyncResult | null;
  error: string | null;
}

/** Why the user-facing label is what it is — the label itself collapses several distinct states into one sentence. */
export type LookupSyncStatusReason =
  | 'checking-own-run'
  | 'queued-behind-run'
  | 'mirroring-other-context'
  | 'syncing'
  | 'failed'
  | 'completed-with-errors'
  | 'completed'
  | 'idle-error'
  | 'idle-last-run-failed'
  | 'idle-up-to-date'
  | 'idle-never-run';

/** Single source of truth for what every module surface (profile icon, progress bar) displays. */
export interface LookupSyncModuleStatus extends LookupSyncModuleView {
  percent: number;
  label: string;
  isActive: boolean;
  hasErrors: boolean;
  failedTables: string[];
  reason: LookupSyncStatusReason;
  reasonDetail: string;
}

export type LookupSyncQueueEntryState = 'queued' | 'running' | 'completed' | 'failed' | 'skipped';

export interface LookupSyncQueueEntry {
  id: string;
  /** Module that requested the sync, or 'common' for shell-wide runs. */
  module: string;
  source: LookupSyncTriggerSource;
  state: LookupSyncQueueEntryState;
  requestId: string | null;
  queuedAt: number;
  startedAt: number | null;
  endedAt: number | null;
  note: string | null;
}

export const LOOKUP_BACKGROUND_SYNC_CONFIG = new InjectionToken<LookupBackgroundSyncConfig>(
  'LOOKUP_BACKGROUND_SYNC_CONFIG'
);

const DEFAULT_BACKGROUND_SYNC_MIN_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_TABLE_FETCH_CONCURRENCY = 10;
const DEFAULT_FULL_DATA_MAX_AGE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
// Budget for a whole run, armed once and never refreshed: large tables (drug/icd/loinc) download,
// clone across the worker boundary and write with no progress messages in between.
const DEFAULT_REQUEST_TIMEOUT_MS = 6 * 60 * 1000;
// Empty by default: every module is allowed to trigger its own lookup sync on navigation
// (isModuleEnabled() treats an empty list as "all modules enabled"). Restrict via
// LOOKUP_BACKGROUND_SYNC_CONFIG.enabledModules if only specific modules should sync.
const DEFAULT_ENABLED_MODULES: string[] = [];
const COMMON_CONTEXT_KEY = '__common__';
const MODULE_NAVIGATION_DEBOUNCE_MS = 300;
const SERVICE_WORKER_READY_TIMEOUT_MS = 15000;
const MAX_SERVICE_WORKER_READY_RETRY_ATTEMPTS = 1;

/** Floor between cache-miss recoveries so an empty server-side table set can't cause a sync loop. */
const CACHE_MISS_RETRY_MIN_INTERVAL_MS = 15000;
const BUSY_RETRY_DELAY_MS = 1500;
/** A worker run orphaned by a page reload never reports back, so busy retries must give up instead of looping. */
const MAX_BUSY_RETRY_ATTEMPTS = 5;
const MAX_BUSY_RETRY_DELAY_MS = 20000;
const REQUEST_ID_SEPARATOR = '::';
/** Set to 'true' in localStorage to enable verbose console logging and the in-app sync debug panel. */
export const LOOKUP_SYNC_DEBUG_STORAGE_KEY = 'symphony.lookupSync.debug';
const LOOKUP_SYNC_QUEUE_HISTORY_LIMIT = 25;

/** Config timestamps change rarely, so rapid module switches can reuse the last response instead of re-fetching. */
const LOOKUP_CONFIG_CACHE_TTL_MS = 60000;

interface LookupConfigRow {
  endpoint?: string;
  lookupName?: string;
  createdDate?: string;
  modifiedDate?: string;
}

interface SharedLookupSyncStatus {
  state: ReturnType<typeof signal<LookupBackgroundState>>;
  progress: ReturnType<typeof signal<LookupBackgroundSyncProgress>>;
  lastRun: ReturnType<typeof signal<LookupBackgroundSyncResult | null>>;
  lastError: ReturnType<typeof signal<string | null>>;
}

function getSharedLookupSyncStatus(): SharedLookupSyncStatus {
  const key = '__LOOKUP_BACKGROUND_SYNC_STATUS__';
  const globalStore = globalThis as typeof globalThis & {
    [key]?: SharedLookupSyncStatus;
  };

  if (!globalStore[key]) {
    globalStore[key] = {
      state: signal<LookupBackgroundState>('idle'),
      progress: signal<LookupBackgroundSyncProgress>({
        total: 0,
        completed: 0,
        failed: 0,
        skipped: 0,
        currentTable: null,
        details: [],
      }),
      lastRun: signal<LookupBackgroundSyncResult | null>(null),
      lastError: signal<string | null>(null),
    };
  }

  return globalStore[key]!;
}

interface LookupTableScopeRule {
  modules?: string[];
  preloadOnShellInit?: boolean;
  /**
   * When true, the table is never touched by the recurring background-sync engine (startup/online/focus/
   * visible/manual/module triggers, including forceRefresh and loadFullDataOnStart). Use this for tables
   * whose IndexedDB copy is instead populated on demand elsewhere (e.g. the per-assessment FHIR-analyze
   * download flow via LookupService.refreshLookupTableIfStale).
   */
  excludeFromSync?: boolean;
}

const DEFAULT_TABLE_SCOPE_RULE: LookupTableScopeRule = {
  preloadOnShellInit: true,
};

// Generic table-scope policy. Unlisted tables use DEFAULT_TABLE_SCOPE_RULE.
// loinc-codes is live API only (see LIVE_API_ONLY_LOOKUPS in lookup.service.ts) and never reaches this sync.
//
// icd-codes/drug-codes/clients/vendors are intentionally excluded from this recurring sync: icd/drug are
// downloaded per-assessment during the offline download flow (AssessmentCacheService.download ->
// LookupService.refreshLookupTableIfStale), driven by whether the assessment is offline-ready — not by
// generic module navigation or periodic timers. clients/vendors are administrative lists that must reflect
// live server state, so they're read straight from the API (see NETWORK_FIRST_LOOKUPS in lookup.service.ts)
// and are only cached in IndexedDB when an offline download explicitly pulls them in for dashboard filters.
const LOOKUP_TABLE_SCOPE_RULES: Record<string, LookupTableScopeRule> = {
  'icd-codes': {
    modules: ['assessment', 'template', 'admin'],
    preloadOnShellInit: false,
    excludeFromSync: true,
  },
  'drug-codes': {
    modules: ['assessment', 'template'],
    preloadOnShellInit: false,
    excludeFromSync: true,
  },
  clients: {
    preloadOnShellInit: false,
    excludeFromSync: true,
  },
  vendors: {
    preloadOnShellInit: false,
    excludeFromSync: true,
  },
};

export type LookupSyncTriggerSource = 'startup' | 'online' | 'focus' | 'visible' | 'manual' | 'module';

type LookupSwMessage =
  | {
      type: 'LOOKUP_SYNC_TABLE_DATA';
      requestId?: string;
      payload?: {
        writeKey?: string;
        tableName?: string;
        rows?: unknown[];
        metaData?: {
          modifiedDate?: string;
        };
      };
    }
  | {
      type: 'LOOKUP_SYNC_STARTED';
      requestId?: string;
      total?: number;
      tables?: string[];
    }
  | {
      type: 'LOOKUP_SYNC_TABLE_WRITING';
      requestId?: string;
      tableName?: string;
      activeTables?: string[];
    }
  | {
      type: 'LOOKUP_SYNC_TABLE_COMPLETED';
      requestId?: string;
      tableName?: string;
      rowCount?: number;
      dbDurationMs?: number;
    }
  | {
      type: 'LOOKUP_SYNC_TABLE_FAILED';
      requestId?: string;
      tableName?: string;
      reason?: string;
    }
  | {
      type: 'LOOKUP_SYNC_TABLE_SKIPPED';
      requestId?: string;
      tableName?: string;
      reason?: string;
    }
  | {
      type: 'LOOKUP_SYNC_COMPLETE';
      requestId?: string;
      startedAt?: number;
      endedAt?: number;
    }
  | {
      type: 'LOOKUP_SYNC_FAILED';
      requestId?: string;
      error?: string;
    }
  | {
      type: 'LOOKUP_SYNC_CANCELLED';
      requestId?: string;
      error?: string;
    }
  | {
      type: 'LOOKUP_SYNC_BUSY';
      requestId?: string;
      reason?: string;
    };

@Injectable({ providedIn: 'root' })
export class LookupBackgroundSyncService {
  private readonly lookupService = inject(LookupService);
  private readonly http = inject(HttpClient);
  private readonly authStateService = inject(AuthStateService, {
    optional: true,
  });
  private readonly impersonationService = inject(ImpersonationService, {
    optional: true,
  });
  private readonly destroyRef = inject(DestroyRef);
  private readonly config = inject(LOOKUP_BACKGROUND_SYNC_CONFIG, {
    optional: true,
  });

  private readonly sharedStatus = getSharedLookupSyncStatus();
  readonly state = this.sharedStatus.state;
  readonly progress = this.sharedStatus.progress;
  readonly lastRun = this.sharedStatus.lastRun;
  readonly lastError = this.sharedStatus.lastError;
  readonly status = computed(() => this.buildGlobalSyncStatus());
  private readonly lastRunByContext = signal<Record<string, LookupBackgroundSyncResult>>({});
  private readonly lastErrorByContext = signal<Record<string, string | null>>({});

  private isRunning = false;
  private pendingRequestId: string | null = null;
  private pendingRunPromise: Promise<void> | null = null;
  private resolvePendingRun: (() => void) | null = null;
  private rejectPendingRun: ((error: unknown) => void) | null = null;
  private forceRefresh = false;
  /**
   * True for the duration of an explicit hardRefresh() call, including its initial "checking" phase — lets UI keep the
   * progress banner visible instead of hiding it until a download actually starts.
   */
  private readonly manualRefreshSignal = signal(false);
  readonly isManualRefreshActive = this.manualRefreshSignal.asReadonly();
  private completionTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRequestedAt = 0;
  private readonly abortController = new AbortController();
  private readonly activeWritingTables = new Set<string>();
  private activeModuleSignal = signal<string | null>(null);
  /**
   * Single source of truth for "current module" — consumers should use this (or getSyncStatusForActiveModule()) instead
   * of independently re-deriving it from the URL.
   */
  readonly activeModule = this.activeModuleSignal.asReadonly();
  private pendingContextKey: string | null = null;

  /** Context that owns the in-flight run; with no run active, fall back to the module the user is on. */
  private get activeContextKey(): string {
    return this.pendingContextKey ?? this.resolveContextKeyForModule(this.activeModule());
  }
  private pendingSource: LookupSyncTriggerSource | null = null;
  /** Request id of a run owned by another service instance that this one mirrors. */
  private followedRequestId: string | null = null;
  private readonly queuedRequests = signal<
    readonly { contextKey: string; source: LookupSyncTriggerSource; queueEntryId: string | null }[]
  >([]);
  private readonly queuedContextKeys = computed(() => this.queuedRequests().map((request) => request.contextKey));
  private readonly syncQueueSignal = signal<readonly LookupSyncQueueEntry[]>([]);
  /** Debug feed: every sync request in arrival order, labelled by the module that asked for it. */
  readonly syncQueue = this.syncQueueSignal.asReadonly();
  readonly pendingQueueEntries = computed(() =>
    this.syncQueue().filter((entry) => entry.state === 'queued' || entry.state === 'running')
  );
  private queueEntrySeq = 0;
  private busyRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private busyRetryAttempts = 0;
  private moduleNavigationDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingNavigationQueueEntryId: string | null = null;
  private pendingRunQueueEntryId: string | null = null;
  private readonly serviceWorkerReadyRetryAttempts = new Map<string, number>();
  private cacheProbeInFlight = false;
  private lastCacheMissSyncAt = 0;
  private cachedLookupConfigs: LookupConfigRow[] | null = null;
  private cachedLookupConfigsAt = 0;
  private inFlightLookupConfigs: Promise<LookupConfigRow[]> | null = null;
  /** Guards the cache-complete marker so a run that skipped the common tables can't claim a full cache. */
  private pendingCoversCommonTables = false;

  constructor() {
    this.registerTriggers();
    this.registerServiceWorkerMessages();

    if (typeof window !== 'undefined') {
      void this.evaluateFullDataOnStart();
    }

    this.destroyRef.onDestroy(() => {
      this.clearCompletionTimer();
      if (this.moduleNavigationDebounceTimer) {
        clearTimeout(this.moduleNavigationDebounceTimer);
      }
      if (this.busyRetryTimer) {
        clearTimeout(this.busyRetryTimer);
      }
      if (this.rejectPendingRun) {
        this.rejectPendingRun(new Error('Lookup background sync service destroyed'));
      }
      this.abortController.abort();
    });
  }

  /**
  * "Load full data on start" mode: decides once at boot whether every configured lookup table is already
   * cached and fresh enough (skip entirely), stale (hard reload), or incomplete (background-load whatever
   * is missing). Runs through the same service-worker pipeline as any other sync, so the fetch/write work
   * happens off the main thread.
   */
  private async evaluateFullDataOnStart(): Promise<void> {
    if (!this.getLoadFullDataOnStart() || !this.getIsEnabled()) {
      return;
    }

    const allTables = await this.lookupService.getFullPreloadLookupTableNames();

    if (allTables.length === 0) {
      this.logDebug('Full-data-on-start: no persisted lookup-config table set, loading in the background');
      void this.startBackgroundSync('startup', COMMON_CONTEXT_KEY);
      return;
    }

    const snapshot = await this.lookupService.getLookupSyncMetaSnapshot(allTables);
    const allLoaded = allTables.every((tableName) => snapshot[tableName] !== undefined);

    if (!allLoaded) {
      this.logDebug('Full-data-on-start: cache incomplete, loading missing tables in the background');
      void this.startBackgroundSync('startup', COMMON_CONTEXT_KEY);
      return;
    }

    const oldestSyncEpochMs = Math.min(
      ...allTables.map((tableName) => snapshot[tableName]?.lastSyncEpochMs ?? 0)
    );
    const ageDays = (Date.now() - oldestSyncEpochMs) / DAY_MS;

    if (ageDays >= this.getFullDataMaxAgeDays()) {
      this.logDebug('Full-data-on-start: cache is stale, checking for server updates', { ageDays });
      await this.startBackgroundSync('startup', COMMON_CONTEXT_KEY);
      return;
    }

    const now = Date.now();
    const cacheReadyResult: LookupBackgroundSyncResult = {
      total: allTables.length,
      completed: 0,
      failed: 0,
      skipped: allTables.length,
      currentTable: null,
      details: [],
      plannedTables: allTables,
      startedAt: now,
      endedAt: now,
    };
    this.lastError.set(null);
    this.lastRun.set(cacheReadyResult);
    this.updateContextResult(this.resolveContextKeyForSource('startup'), cacheReadyResult);
    this.logDebug('Full-data-on-start: cache is complete and fresh, skipping startup sync', { ageDays });
  }

  setActiveModule(moduleName: string | null | undefined): void {
    // Full-data-on-start owns freshness end-to-end; navigation never re-checks or re-syncs in this mode.
    if (this.getLoadFullDataOnStart()) {
      this.activeModuleSignal.set(normalizeModuleName(moduleName));
      return;
    }

    const normalized = normalizeModuleName(moduleName);
    const moduleChanged = this.activeModule() !== normalized;
    const contextKey = this.resolveContextKeyForModule(normalized);

    this.activeModuleSignal.set(normalized);

    if (!normalized || !this.isModuleEnabled(normalized)) {
      this.recordSkippedRequest(
        normalized ?? COMMON_CONTEXT_KEY,
        'module',
        normalized ? 'module is not in enabledModules' : `unrecognised route segment "${moduleName ?? ''}"`
      );
      return;
    }

    if (this.moduleNavigationDebounceTimer) {
      clearTimeout(this.moduleNavigationDebounceTimer);
      this.updateQueueEntryById(this.pendingNavigationQueueEntryId, {
        state: 'skipped',
        endedAt: Date.now(),
        note: 'module navigation superseded before the precheck started',
      });
    }

    this.pendingNavigationQueueEntryId = this.isDebugLoggingEnabled()
      ? this.pushQueueEntry(contextKey, 'module', 'queued', 'module navigation received - waiting for debounce')
      : null;

    this.moduleNavigationDebounceTimer = setTimeout(() => {
      this.moduleNavigationDebounceTimer = null;
      const queueEntryId = this.pendingNavigationQueueEntryId;
      this.pendingNavigationQueueEntryId = null;
      // Set before starting the precheck: a synchronous skip inside it must be able to overwrite
      // this note with the real reason, not the other way around.
      this.updateQueueEntryById(queueEntryId, {
        note: 'module navigation precheck started',
      });
      // Fire-and-forget: an unhandled rejection here would freeze the debug entry at "queued" forever.
      this.syncOnModuleNavigation(moduleChanged, contextKey, queueEntryId).catch((error) => {
        const message = error instanceof Error ? error.message : 'Module navigation precheck failed';
        console.error('[Lookup sync] Module navigation precheck failed', error);
        this.updateQueueEntryById(queueEntryId, {
          state: 'failed',
          endedAt: Date.now(),
          note: message,
        });
      });
    }, MODULE_NAVIGATION_DEBOUNCE_MS);
  }

  private async syncOnModuleNavigation(
    moduleChanged: boolean,
    contextKey: string,
    queueEntryId: string | null
  ): Promise<void> {
    if (!moduleChanged) {
      await this.syncIfCacheMissing(contextKey, queueEntryId);
      return;
    }

    // A sync is already in flight, so this request will queue regardless — skip the cache check
    // (its IndexedDB reads can be slow while the active sync is writing) and show the real reason now.
    if (this.isRunning) {
      this.updateQueueEntryById(queueEntryId, {
        state: 'queued',
        note: 'queued behind active lookup sync',
      });
      this.enqueueSync('module', contextKey, false, queueEntryId);
      return;
    }

    // A cached module still costs a full worker round-trip (config fetch + meta snapshot + a skip
    // message per table), so check locally first. Freshness still comes from startup/online/focus runs.
    if (await this.isModuleCacheSatisfied(contextKey)) {
      this.recordSkippedRequest(contextKey, 'module', 'module change — cache already covers this module', queueEntryId);
      return;
    }

    // Module-scoped tables are additive: every module also needs the common tables, so sync the
    // full allowed set. Tables already cached are dropped by the not-modified precheck.
    await this.startBackgroundSync('module', contextKey, queueEntryId);
  }

  /** True when the shell-preload marker is present and all required tables for this module have already synced. */
  private async isModuleCacheSatisfied(contextKey = this.resolveContextKeyForSource('module')): Promise<boolean> {
    if (this.forceRefresh) {
      return false;
    }

    if (!(await this.lookupService.hasCompleteLookupCache())) {
      return false;
    }

    const requiredTables = this.resolveAllowedTables('module', contextKey);

    if (requiredTables.length === 0) {
      return true;
    }

    const snapshot = await this.lookupService.getLookupSyncMetaSnapshot(requiredTables);

    // A meta row means the table synced before; count can be 0 for legitimately empty lookups.
    return requiredTables.every((tableName) => snapshot[tableName] !== undefined);
  }

  /**
   * Same-module navigation normally needs no sync, but site data can be cleared mid-session. Probe the cache so the
   * lookups refill without waiting for a full page reload.
   */
  private async syncIfCacheMissing(
    contextKey = this.resolveContextKeyForSource('module'),
    queueEntryId: string | null = null
  ): Promise<void> {
    if (!this.getIsEnabled() || this.isRunning || this.cacheProbeInFlight) {
      this.recordSkippedRequest(
        contextKey,
        'module',
        'same-module navigation — a run or cache probe is already in progress',
        queueEntryId
      );
      return;
    }

    const now = Date.now();
    if (now - this.lastCacheMissSyncAt < CACHE_MISS_RETRY_MIN_INTERVAL_MS) {
      this.recordSkippedRequest(
        contextKey,
        'module',
        'same-module navigation — cache re-probe throttled (15s)',
        queueEntryId
      );
      return;
    }

    this.cacheProbeInFlight = true;

    try {
      const allowedTables = this.resolveAllowedTables('module', contextKey);
      if (allowedTables.length === 0) {
        this.recordSkippedRequest(contextKey, 'module', 'same-module navigation — no tables in scope', queueEntryId);
        return;
      }

      if (await this.lookupService.hasCompleteLookupCache()) {
        this.recordSkippedRequest(
          contextKey,
          'module',
          'same-module navigation — cache already complete',
          queueEntryId
        );
        return;
      }

      this.logDebug('Lookup cache is empty, starting sync', {
        module: this.activeModule(),
        allowedTableCount: allowedTables.length,
      });

      this.lastCacheMissSyncAt = Date.now();
      await this.startBackgroundSync('module', contextKey, queueEntryId);
    } finally {
      this.cacheProbeInFlight = false;
    }
  }

  async startBackgroundSync(
    source: LookupSyncTriggerSource = 'manual',
    contextKeyOverride?: string,
    queueEntryId: string | null = null
  ): Promise<void> {
    this.logDebug('Sync requested', { source, contextKeyOverride });

    if (!this.getIsEnabled()) {
      this.logDebug('Sync skipped because it is disabled');
      this.abandonQueuedRun(source, contextKeyOverride, 'skipped: sync disabled');
      return;
    }

    const contextKey = contextKeyOverride ?? this.resolveContextKeyForSource(source);
    if (!this.canSyncForSource(source, contextKey)) {
      this.logDebug('Sync skipped because the source is disabled', { source });
      this.abandonQueuedRun(source, contextKeyOverride, 'skipped: trigger source disabled');
      this.state.set('idle');
      return;
    }

    if (this.isRunning) {
      this.logDebug('Sync request queued because another run is active', {
        source,
      });
      this.updateQueueEntryById(queueEntryId, {
        state: 'queued',
        note: 'queued behind active lookup sync',
      });
      this.enqueueSync(source, contextKey, !queueEntryId, queueEntryId);
      return;
    }

    const now = Date.now();
    const shouldThrottle =
      source !== 'manual' && source !== 'module' && now - this.lastRequestedAt < this.getMinIntervalMs();
    if (shouldThrottle) {
      this.logDebug('Sync request was throttled', { source });
      this.abandonQueuedRun(source, contextKeyOverride, 'skipped: throttled');
      return;
    }

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    if (!isOnline) {
      this.logDebug('Sync skipped because the browser is offline');
      this.abandonQueuedRun(source, contextKeyOverride, 'skipped: offline');
      this.state.set('idle');
      return;
    }

    this.isRunning = true;
    this.lastError.set(null);
    this.state.set('checking');
    this.resetProgress(0);

    const supportsServiceWorkerSync =
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof navigator.serviceWorker.addEventListener === 'function';

    if (!supportsServiceWorkerSync) {
      this.logDebug('Sync failed because service workers are unavailable');
      this.handleRunFailure(new Error('Service worker background sync is not available'));
      return;
    }

    try {
      const allowedTables = this.resolveAllowedTables(source, contextKey);
      this.pendingCoversCommonTables = this.coversCommonTables(allowedTables);
      const requestId = this.createRequestId(source, contextKey);
      this.pendingRequestId = requestId;
      this.followedRequestId = null;
      this.pendingContextKey = contextKey;
      this.pendingSource = source;
      this.pendingRunQueueEntryId = queueEntryId;
      this.dequeueContextKey(contextKey);
      this.updateRunQueueEntry(contextKey, source, {
        state: 'running',
        source,
        requestId,
        startedAt: Date.now(),
        note: 'waiting for service worker readiness',
      });
      this.updateContextError(contextKey, null);
      this.startCompletionTimer(requestId);

      this.logDebug('Waiting for service worker readiness', {
        requestId,
        contextKey,
        allowedTableCount: allowedTables.length,
      });

      const lookupApiBaseUrl = this.lookupService.getEffectiveBaseUrl();
      this.lastRequestedAt = now;

      if (!this.shouldUseServiceWorker()) {
        await this.runForegroundSync(requestId, allowedTables, lookupApiBaseUrl);
        return;
      }

      const registration = await this.waitForServiceWorkerReady();
      this.serviceWorkerReadyRetryAttempts.delete(contextKey);
      if (this.pendingRequestId !== requestId) {
        this.logDebug('Sync ended before the service worker became ready', {
          requestId,
        });
        return;
      }

      const worker = navigator.serviceWorker.controller ?? registration.active;

      if (!worker) {
        this.handleRunFailure(new Error('No active service worker controller for lookup sync'));
        return;
      }

      const tableFetchConcurrency = this.getTableFetchConcurrency();

      if (allowedTables.length === 0) {
        const nowEpoch = Date.now();
        this.updateContextResult(contextKey, {
          total: 0,
          completed: 0,
          failed: 0,
          skipped: 0,
          currentTable: null,
          details: [],
          startedAt: nowEpoch,
          endedAt: nowEpoch,
        });
        this.updateContextError(contextKey, null);
        this.finishQueueEntry(contextKey, 'completed', 'no tables in scope for this module');
        this.state.set('idle');
        this.resetPendingState();
        this.isRunning = false;
        this.drainQueuedSync();
        return;
      }

      let tableMetaSnapshot: Record<
        string,
        {
          count: number;
          sourceModifiedEpochMs?: number;
        }
      > = {};

      if (!this.forceRefresh) {
        tableMetaSnapshot = await this.lookupService.getLookupSyncMetaSnapshot(allowedTables);
        if (this.pendingRequestId !== requestId) {
          this.logDebug('Sync ended while reading local lookup metadata', {
            requestId,
          });
          return;
        }
      }

      this.pendingRunPromise = new Promise<void>((resolve, reject) => {
        this.resolvePendingRun = resolve;
        this.rejectPendingRun = reject;
      });

      this.logDebug('Posting sync request to the service worker', {
        requestId,
        allowedTableCount: allowedTables.length,
        tableMetaCount: Object.keys(tableMetaSnapshot).length,
      });

      worker.postMessage({
        type: 'LOOKUP_SYNC_START',
        payload: {
          requestId,
          source,
          activeOnly: true,
          lookupApiBaseUrl,
          tableFetchConcurrency,
          allowedTables,
          tableMetaSnapshot,
          authHeaders: this.buildServiceWorkerAuthHeaders(),
        },
      });

      await this.pendingRunPromise;
    } catch (error) {
      // Order matters: enqueue the retry (if any) before handleRunFailure's single drain call, so
      // draining doesn't run twice and reorder the FIFO queue.
      this.retryAfterServiceWorkerReadyFailure(error, source, contextKeyOverride);
      this.handleRunFailure(error);
    } finally {
      if (this.pendingRequestId === null) {
        this.isRunning = false;
      }
    }
  }

  async hardRefresh(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    this.forceRefresh = true;
    this.manualRefreshSignal.set(true);
    try {
      await this.startBackgroundSync('manual');
    } finally {
      this.manualRefreshSignal.set(false);
    }
  }

  private registerTriggers(): void {
    const abortSignal = this.abortController.signal;

    if (typeof window === 'undefined') {
      return;
    }

    if (this.getAutoStartOnOnline()) {
      window.addEventListener(
        'online',
        () => {
          void this.startBackgroundSync('online');
        },
        { signal: abortSignal }
      );
    }

    // Re-verify on tab return: catches data removed out-of-band (e.g. manually cleared via
    // DevTools) since the SW re-fetches any table whose local row count is 0.
    if (this.getAutoStartOnVisible() && !this.getLoadFullDataOnStart() && typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'visible') {
            void this.startBackgroundSync('visible');
          }
        },
        { signal: abortSignal }
      );
    }
  }

  private registerServiceWorkerMessages(): void {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener(
      'message',
      (event: MessageEvent) => {
        void this.processServiceWorkerMessage(event);
      },
      { signal: this.abortController.signal }
    );
  }

  private async processServiceWorkerMessage(event: MessageEvent): Promise<void> {
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }

    const message = data as LookupSwMessage;
    const requestId = typeof message.requestId === 'string' ? message.requestId : null;

    if (!requestId) {
      return;
    }

    this.logDebug('Received service worker sync message', {
      type: message.type,
      requestId,
      tableName: 'tableName' in message ? String(message.tableName ?? '') : undefined,
    });

    if (message.type === 'LOOKUP_SYNC_TABLE_DATA') {
      await this.handleLookupTableData(message, event.source);
      return;
    }

    if (this.pendingRequestId) {
      if (requestId !== this.pendingRequestId) {
        return;
      }
    } else if (!this.adoptForeignRun(requestId, message.type)) {
      return;
    }

    switch (message.type) {
      case 'LOOKUP_SYNC_STARTED': {
        this.busyRetryAttempts = 0;
        this.activeWritingTables.clear();
        const plannedTables = Array.isArray(message.tables)
          ? message.tables.map((table: unknown) => String(table ?? '').trim()).filter(Boolean)
          : [];
        this.resetProgress(Number(message.total ?? 0), plannedTables);
        // The worker is now actually running; the debug note must not still say "waiting for readiness".
        this.updateRunQueueEntry(this.activeContextKey, this.pendingSource ?? 'module', {
          note: `downloading required data`,
        });
        break;
      }

      case 'LOOKUP_SYNC_TABLE_WRITING': {
        this.state.set('syncing');
        const tableName = String(message.tableName ?? '').trim();
        if (tableName) {
          this.activeWritingTables.add(tableName);
        }

        const activeTablesFromSw = Array.isArray(message.activeTables)
          ? message.activeTables.map((t) => String(t ?? '').trim()).filter(Boolean)
          : [];

        if (activeTablesFromSw.length > 0) {
          this.activeWritingTables.clear();
          for (const table of activeTablesFromSw) {
            this.activeWritingTables.add(table);
          }
        }

        this.updateCurrentTablesLabel();
        break;
      }

      case 'LOOKUP_SYNC_TABLE_COMPLETED': {
        const tableName = String(message.tableName ?? '');
        const rowCount = Number(message.rowCount ?? 0);
        const dbDurationMs = Number(message.dbDurationMs);
        this.activeWritingTables.delete(tableName);
        this.updateCurrentTablesLabel();

        this.applyProgressDetail({
          tableName,
          status: 'completed',
          rowCount,
          durationMs: Number.isFinite(dbDurationMs) ? dbDurationMs : undefined,
        });
        break;
      }

      case 'LOOKUP_SYNC_TABLE_FAILED': {
        const tableName = String(message.tableName ?? 'unknown');
        this.activeWritingTables.delete(tableName);
        this.updateCurrentTablesLabel();
        this.applyProgressDetail({
          tableName,
          status: 'failed',
          reason: String(message.reason ?? 'Lookup fetch failed in service worker'),
        });
        break;
      }

      case 'LOOKUP_SYNC_TABLE_SKIPPED': {
        const tableName = String(message.tableName ?? 'unknown');
        this.applyProgressDetail({
          tableName,
          status: 'skipped_not_modified',
          reason: String(message.reason ?? 'Not modified (SW precheck)'),
        });
        break;
      }

      case 'LOOKUP_SYNC_COMPLETE': {
        this.finishRun(Number(message.startedAt ?? Date.now()), Number(message.endedAt ?? Date.now()));
        break;
      }

      case 'LOOKUP_SYNC_FAILED': {
        this.handleRunFailure(new Error(String(message.error ?? 'Lookup sync failed in service worker')));
        break;
      }

      case 'LOOKUP_SYNC_CANCELLED': {
        this.handleRunFailure(new Error(String(message.error ?? 'Lookup sync cancelled for a service worker update')));
        break;
      }

      case 'LOOKUP_SYNC_BUSY': {
        this.handleServiceWorkerBusy();
        break;
      }

      default:
        break;
    }
  }

  private async handleLookupTableData(
    message: Extract<LookupSwMessage, { type: 'LOOKUP_SYNC_TABLE_DATA' }>,
    source: MessageEventSource | null
  ): Promise<void> {
    const payload = message.payload;
    const writeKey = payload?.writeKey;
    const tableName = payload?.tableName;

    if (!writeKey || !tableName || !Array.isArray(payload.rows)) {
      return;
    }

    try {
      const result = await this.lookupService.applyBackgroundLookupTable(tableName, payload.rows, {
        modifiedDate: payload.metaData?.modifiedDate,
        forceRefresh: true,
      });

      source?.postMessage({
        type: 'LOOKUP_SYNC_TABLE_WRITE_RESULT',
        payload: { writeKey, success: result.status === 'completed' },
      });
    } catch (error) {
      source?.postMessage({
        type: 'LOOKUP_SYNC_TABLE_WRITE_RESULT',
        payload: {
          writeKey,
          success: false,
          error: error instanceof Error ? error.message : 'Dexie lookup write failed',
        },
      });
    }
  }

  /**
   * Another instance of this service owns the run. Mirror it so every surface shows live progress instead of a stale
   * state.
   */
  private adoptForeignRun(requestId: string, messageType: string): boolean {
    if (this.followedRequestId === requestId) {
      return true;
    }

    // Terminal message for a run we never mirrored: just release our queued work.
    if (messageType === 'LOOKUP_SYNC_COMPLETE' || messageType === 'LOOKUP_SYNC_FAILED') {
      this.isRunning = false;
      this.drainQueuedSync();
      return false;
    }

    if (messageType === 'LOOKUP_SYNC_BUSY') {
      return false;
    }

    this.followedRequestId = requestId;
    this.pendingContextKey = this.parseContextKeyFromRequestId(requestId);
    this.busyRetryAttempts = 0;
    this.dequeueContextKey(this.pendingContextKey);
    this.updateQueueEntry(this.pendingContextKey, this.pendingSource ?? 'module', {
      state: 'running',
      requestId,
      startedAt: Date.now(),
      note: 'mirroring run owned by another tab/instance',
    });
    this.isRunning = true;
    this.activeWritingTables.clear();
    this.resetProgress(0);
    this.state.set('syncing');
    this.startCompletionTimer(requestId);

    return true;
  }

  private resetProgress(total: number, plannedTables: string[] = []): void {
    this.progress.set({
      total,
      completed: 0,
      failed: 0,
      skipped: 0,
      currentTable: null,
      details: [],
      plannedTables,
    });
  }

  private applyProgressDetail(detail: LookupSyncTableProgress): void {
    this.progress.update((p) => {
      // The worker can re-announce a table; count each one once per run.
      if (p.details.some((existing) => existing.tableName === detail.tableName)) {
        return p;
      }

      let completed = p.completed;
      let failed = p.failed;
      let skipped = p.skipped;

      if (detail.status === 'completed') {
        completed += 1;
      } else if (detail.status === 'failed') {
        failed += 1;
      } else {
        skipped += 1;
      }

      const total = Math.max(p.total, completed + failed + skipped);

      return {
        ...p,
        total,
        completed,
        failed,
        skipped,
        details: [...p.details, detail],
      };
    });
  }

  private finishRun(startedAt: number, endedAt: number): void {
    const snapshot = this.progress();
    const source = this.pendingSource;
    this.activeWritingTables.clear();
    const runResult: LookupBackgroundSyncResult = {
      ...snapshot,
      startedAt,
      endedAt,
      currentTable: null,
    };

    this.lastRun.set(runResult);
    this.updateContextResult(this.activeContextKey, runResult);
    this.updateContextError(this.activeContextKey, null);

    this.progress.update((p) => ({ ...p, currentTable: null }));
    this.state.set(snapshot.failed > 0 ? 'failed' : 'completed');

    if (snapshot.failed === 0 && this.pendingCoversCommonTables) {
      const configTableNames =
        source === 'startup' && this.getLoadFullDataOnStart() ? snapshot.plannedTables : undefined;
      void this.lookupService.markLookupCacheComplete(snapshot.total, configTableNames);
    }
    if (snapshot.failed === 0 && source === 'startup' && this.getLoadFullDataOnStart()) {
      const unchangedTables = snapshot.details
        .filter((detail) => detail.status === 'skipped_not_modified')
        .map((detail) => detail.tableName);
      void this.lookupService.markLookupTablesChecked(unchangedTables);
    }
    this.logDebug('Sync finished', {
      requestId: this.pendingRequestId ?? this.followedRequestId,
      completed: snapshot.completed,
      failed: snapshot.failed,
      skipped: snapshot.skipped,
      total: snapshot.total,
    });

    this.finishQueueEntry(
      this.activeContextKey,
      snapshot.failed > 0 ? 'failed' : 'completed',
      `${snapshot.completed} loaded / ${snapshot.skipped} cached / ${snapshot.failed} failed`
    );

    this.resolvePendingRun?.();
    this.resetPendingState();
    this.isRunning = false;

    if (this.state() === 'completed' || this.state() === 'failed') {
      setTimeout(() => {
        if (this.state() === 'completed' || this.state() === 'failed') {
          this.state.set('idle');
        }
      }, 2500);
    }

    this.drainQueuedSync();
  }

  private handleRunFailure(error: unknown): void {
    const message = error instanceof Error ? error.message : 'Lookup sync failed';
    this.logDebug('Sync failed', {
      requestId: this.pendingRequestId ?? this.followedRequestId,
      error: message,
    });
    this.activeWritingTables.clear();
    this.lastError.set(message);
    this.updateContextError(this.activeContextKey, message);
    this.finishQueueEntry(this.activeContextKey, 'failed', message);
    this.state.set('failed');
    // A readiness timeout is retried automatically; only a genuine failure should invalidate cached meta.
    if (message !== 'Service worker did not become ready for lookup sync') {
      this.lookupService.clearDeferredLookupMeta();
    }
    this.rejectPendingRun?.(error);
    this.resetPendingState();
    this.isRunning = false;
    setTimeout(() => {
      if (this.state() === 'failed') {
        this.state.set('idle');
      }
    }, 2500);
    this.drainQueuedSync();
  }

  private retryAfterServiceWorkerReadyFailure(
    error: unknown,
    source: LookupSyncTriggerSource,
    contextKeyOverride: string | undefined
  ): void {
    if (!(error instanceof Error) || error.message !== 'Service worker did not become ready for lookup sync') {
      return;
    }

    const contextKey = contextKeyOverride ?? this.resolveContextKeyForSource(source);
    const attempts = this.serviceWorkerReadyRetryAttempts.get(contextKey) ?? 0;
    if (attempts >= MAX_SERVICE_WORKER_READY_RETRY_ATTEMPTS) {
      // Give this context a clean slate so a later, unrelated readiness hiccup still gets a retry.
      this.serviceWorkerReadyRetryAttempts.delete(contextKey);
      return;
    }

    this.serviceWorkerReadyRetryAttempts.set(contextKey, attempts + 1);
    this.enqueueSync(source, contextKey);
    this.updateQueueEntry(contextKey, source, {
      state: 'queued',
      note: 'service worker readiness timed out - retrying once',
    });
    // handleRunFailure() (called right after this by the caller) drains the queue exactly once.
  }

  private async runForegroundSync(requestId: string, allowedTables: string[], lookupApiBaseUrl: string): Promise<void> {
    this.logDebug('Starting foreground lookup sync', {
      requestId,
      allowedTableCount: allowedTables.length,
      maxConcurrency: this.getTableFetchConcurrency(),
    });

    const prepareStartedAt = Date.now();
    let configsReceivedAt = prepareStartedAt;
    let metaReadAt = prepareStartedAt;

    // The metadata read only needs the allowed table names, so it does not have to wait on the config request.
    const [configs, tableMetaSnapshot] = await Promise.all([
      this.fetchLookupConfigs(lookupApiBaseUrl).then((result) => {
        configsReceivedAt = Date.now();
        return result;
      }),
      (this.forceRefresh ? Promise.resolve({}) : this.lookupService.getLookupSyncMetaSnapshot(allowedTables)).then(
        (result) => {
          metaReadAt = Date.now();
          return result;
        }
      ),
    ]);

    if (this.pendingRequestId !== requestId) {
      return;
    }

    this.logDebug('Lookup precheck inputs ready', {
      requestId,
      configFetchMs: configsReceivedAt - prepareStartedAt,
      metaReadMs: metaReadAt - prepareStartedAt,
      totalPrepareMs: Date.now() - prepareStartedAt,
    });

    const allowedTableSet = new Set(allowedTables.map((tableName) => normalizeLookupName(tableName)));
    const tables = (Array.isArray(configs) ? configs : [])
      .map((config) => ({
        tableName: normalizeLookupName(config.endpoint ?? config.lookupName),
        modifiedDate: config.modifiedDate ?? config.createdDate,
      }))
      .filter((config) => config.tableName && allowedTableSet.has(config.tableName));

    this.resetProgress(
      tables.length,
      tables.map((table) => table.tableName)
    );
    this.state.set('syncing');

    const startedAt = Date.now();
    const concurrency = Math.max(1, Math.min(this.getTableFetchConcurrency(), tables.length));

    this.logDebug('Downloading lookup tables', {
      requestId,
      tableCount: tables.length,
      concurrency,
      tables: tables.map((table) => table.tableName),
    });

    this.activeWritingTables.clear();

    // Shared cursor lets each worker pull the next table as soon as it frees up.
    let nextTableIndex = 0;
    const runWorker = async (): Promise<void> => {
      while (this.pendingRequestId === requestId) {
        const index = nextTableIndex++;
        const table = tables[index];
        if (!table) {
          return;
        }

        await this.syncForegroundTable(requestId, table, tableMetaSnapshot, lookupApiBaseUrl);
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => runWorker()));

    if (this.pendingRequestId !== requestId) {
      return;
    }

    const endedAt = Date.now();
    const snapshot = this.progress();

    this.logDebug('All lookup tables processed', {
      requestId,
      totalMs: endedAt - startedAt,
      completed: snapshot.completed,
      skipped: snapshot.skipped,
      failed: snapshot.failed,
      total: snapshot.total,
    });

    this.finishRun(startedAt, endedAt);
  }

  private async syncForegroundTable(
    requestId: string,
    table: { tableName: string; modifiedDate?: string },
    tableMetaSnapshot: Record<string, { count: number; sourceModifiedEpochMs?: number }>,
    lookupApiBaseUrl: string
  ): Promise<void> {
    if (this.shouldSkipForegroundFetch(table, tableMetaSnapshot)) {
      this.logDebug('Lookup table skipped — cached copy is current', {
        requestId,
        tableName: table.tableName,
        cachedRowCount: tableMetaSnapshot[table.tableName]?.count,
      });
      this.applyProgressDetail({
        tableName: table.tableName,
        status: 'skipped_not_modified',
        reason: 'Not modified',
        durationMs: 0,
      });
      return;
    }

    const startedAt = Date.now();
    this.activeWritingTables.add(table.tableName);
    this.updateCurrentTablesLabel();

    this.logDebug('Lookup table started', {
      requestId,
      tableName: table.tableName,
    });

    try {
      const rows = await firstValueFrom(
        this.http.get<unknown[]>(`${lookupApiBaseUrl}/api/Lookup/${encodeURIComponent(table.tableName)}`, {
          context: this.buildSilentRequestContext(),
        })
      );
      const fetchedAt = Date.now();
      const normalizedRows = Array.isArray(rows) ? rows : [];

      const result = await this.lookupService.applyBackgroundLookupTable(table.tableName, normalizedRows, {
        modifiedDate: table.modifiedDate,
        forceRefresh: this.forceRefresh,
      });

      const completedAt = Date.now();
      this.logDebug('Lookup table done', {
        requestId,
        tableName: table.tableName,
        status: result.status,
        rowCount: normalizedRows.length,
        downloadMs: fetchedAt - startedAt,
        writeMs: completedAt - fetchedAt,
        totalMs: completedAt - startedAt,
      });

      this.applyProgressDetail({ ...result, durationMs: completedAt - startedAt });
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Lookup request failed';
      this.logDebug('Lookup table failed', {
        requestId,
        tableName: table.tableName,
        totalMs: Date.now() - startedAt,
        error: reason,
      });
      this.applyProgressDetail({
        tableName: table.tableName,
        status: 'failed',
        reason,
        durationMs: Date.now() - startedAt,
      });
    } finally {
      this.activeWritingTables.delete(table.tableName);
      this.updateCurrentTablesLabel();
    }
  }

  /** Sync runs report a single aggregated outcome, so individual table failures must not each raise a toast. */
  private buildSilentRequestContext(): HttpContext {
    return new HttpContext().set(SUPPRESS_ERROR_NOTIFICATION, true);
  }

  /** Serves `lookup-configs` from a short-lived cache and collapses concurrent requests into one. */
  private fetchLookupConfigs(lookupApiBaseUrl: string): Promise<LookupConfigRow[]> {
    const isCacheUsable =
      !this.forceRefresh &&
      this.cachedLookupConfigs !== null &&
      Date.now() - this.cachedLookupConfigsAt < LOOKUP_CONFIG_CACHE_TTL_MS;

    if (isCacheUsable) {
      return Promise.resolve(this.cachedLookupConfigs as LookupConfigRow[]);
    }

    if (this.inFlightLookupConfigs) {
      return this.inFlightLookupConfigs;
    }

    const request = firstValueFrom(
      this.http.get<LookupConfigRow[]>(`${lookupApiBaseUrl}/api/Lookup/lookup-configs?activeOnly=true`, {
        context: this.buildSilentRequestContext(),
      })
    )
      .then((rows) => {
        const normalized = Array.isArray(rows) ? rows : [];
        this.cachedLookupConfigs = normalized;
        this.cachedLookupConfigsAt = Date.now();
        return normalized;
      })
      .finally(() => {
        this.inFlightLookupConfigs = null;
      });

    this.inFlightLookupConfigs = request;
    return request;
  }

  private shouldSkipForegroundFetch(
    table: { tableName: string; modifiedDate?: string },
    tableMetaSnapshot: Record<string, { count: number; sourceModifiedEpochMs?: number }>
  ): boolean {
    if (this.forceRefresh) {
      return false;
    }

    // Note: a table with count 0 can be a legitimately empty lookup (e.g. association-types) that
    // previously synced successfully -- row count alone must not force a re-fetch on every load.
    const localMeta = tableMetaSnapshot[table.tableName];
    if (!localMeta) {
      return false;
    }

    const modifiedDate = table.modifiedDate?.trim();
    if (!modifiedDate) {
      return true;
    }

    const normalizedDate = /(?:z|[+-]\d{2}:\d{2})$/i.test(modifiedDate) ? modifiedDate : `${modifiedDate}Z`;
    const serverModifiedEpochMs = Date.parse(normalizedDate);

    return (
      Number.isNaN(serverModifiedEpochMs) ||
      (localMeta.sourceModifiedEpochMs != null && serverModifiedEpochMs <= localMeta.sourceModifiedEpochMs)
    );
  }

  /** Service worker already has a run in flight: wait for it, then run this context. */
  private handleServiceWorkerBusy(): void {
    const retrySource = this.pendingSource ?? 'module';
    const retryContextKey = this.activeContextKey;
    this.activeWritingTables.clear();
    this.updateContextError(retryContextKey, null);
    this.resolvePendingRun?.();
    this.resetPendingState();
    this.isRunning = false;
    this.busyRetryAttempts += 1;

    if (this.busyRetryAttempts > MAX_BUSY_RETRY_ATTEMPTS) {
      this.logDebug('Giving up on a busy service worker', {
        contextKey: retryContextKey,
        attempts: this.busyRetryAttempts,
      });
      this.updateQueueEntry(retryContextKey, retrySource, {
        state: 'failed',
        endedAt: Date.now(),
        note: `service worker busy after ${MAX_BUSY_RETRY_ATTEMPTS} retries — waiting for the in-flight run`,
      });
      this.dequeueContextKey(retryContextKey);
      this.state.set('idle');
      return;
    }

    this.state.set('checking');

    // Clearing pendingRequestId lets this instance mirror the in-flight run.
    this.enqueueSync(retrySource, retryContextKey, false);
    this.updateQueueEntry(retryContextKey, retrySource, {
      state: 'queued',
      note: `service worker busy — retry ${this.busyRetryAttempts}/${MAX_BUSY_RETRY_ATTEMPTS}`,
    });

    if (this.busyRetryTimer) {
      clearTimeout(this.busyRetryTimer);
    }
    this.busyRetryTimer = setTimeout(
      () => {
        this.busyRetryTimer = null;
        if (!this.isRunning) {
          this.drainQueuedSync();
        }
      },
      Math.min(BUSY_RETRY_DELAY_MS * 2 ** (this.busyRetryAttempts - 1), MAX_BUSY_RETRY_DELAY_MS)
    );
  }

  private enqueueSync(
    source: LookupSyncTriggerSource,
    contextKey = this.resolveContextKeyForSource(source),
    recordEntry = true,
    queueEntryId: string | null = null
  ): void {
    if (this.queuedContextKeys().includes(contextKey)) {
      // A slot for this module is already queued and will cover this request too; settle this
      // entry now instead of leaving it stuck at "queued" forever with no FIFO slot behind it.
      this.updateQueueEntryById(queueEntryId, {
        state: 'skipped',
        endedAt: Date.now(),
        note: 'already queued behind the same active lookup sync',
      });
      return;
    }

    // Every FIFO slot must own a debug entry id it can be resolved by later, otherwise a queued
    // request with no id (visible/online/focus triggers) can never be updated to a terminal state.
    const resolvedQueueEntryId =
      queueEntryId ?? (recordEntry ? this.pushQueueEntry(contextKey, source, 'queued') : null);

    this.queuedRequests.update((requests) => [...requests, { contextKey, source, queueEntryId: resolvedQueueEntryId }]);
  }

  private dequeueContextKey(contextKey: string): void {
    this.queuedRequests.update((requests) =>
      requests.some((request) => request.contextKey === contextKey)
        ? requests.filter((request) => request.contextKey !== contextKey)
        : requests
    );
  }

  /** FIFO drain: each queued module runs with its own context, not whatever module happens to be active now. */
  private drainQueuedSync(): void {
    const next = this.queuedRequests()[0];
    if (!next) {
      return;
    }

    this.dequeueContextKey(next.contextKey);
    setTimeout(() => void this.startBackgroundSync(next.source, next.contextKey, next.queueEntryId), 0);
  }

  /** A drained entry that never ran must still release the queue, otherwise the FIFO stalls behind it. */
  private abandonQueuedRun(source: LookupSyncTriggerSource, contextKey: string | undefined, note: string): void {
    if (contextKey === undefined) {
      this.recordSkippedRequest(this.resolveContextKeyForSource(source), source, note);
      return;
    }

    this.updateQueueEntry(contextKey, source, {
      state: 'skipped',
      endedAt: Date.now(),
      note,
    });
    this.drainQueuedSync();
  }

  private resetPendingState(): void {
    this.clearCompletionTimer();
    this.pendingRequestId = null;
    this.followedRequestId = null;
    this.pendingRunPromise = null;
    this.resolvePendingRun = null;
    this.rejectPendingRun = null;
    this.pendingSource = null;
    this.pendingRunQueueEntryId = null;
    // Callers read pendingContextKey before resetting; leaving it set makes later runs blame the wrong module.
    this.pendingContextKey = null;
    this.pendingCoversCommonTables = false;
    this.forceRefresh = false;
  }

  private clearCompletionTimer(): void {
    if (this.completionTimer) {
      clearTimeout(this.completionTimer);
      this.completionTimer = null;
    }
  }

  private startCompletionTimer(requestId: string): void {
    this.clearCompletionTimer();
    this.completionTimer = setTimeout(() => {
      if (this.pendingRequestId === requestId || this.followedRequestId === requestId) {
        this.handleRunFailure(new Error('Lookup sync timed out in service worker'));
      }
    }, this.getRequestTimeoutMs());
  }

  private async waitForServiceWorkerReady(): Promise<ServiceWorkerRegistration> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      return await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('Service worker did not become ready for lookup sync')),
            SERVICE_WORKER_READY_TIMEOUT_MS
          );
        }),
      ]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  private logDebug(message: string, details?: Record<string, unknown>): void {
    if (!this.isDebugLoggingEnabled()) {
      return;
    }

    console.debug(`[Lookup sync] ${message}`, details ?? {});
  }

  isDebugLoggingEnabled(): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }

    try {
      return localStorage.getItem(LOOKUP_SYNC_DEBUG_STORAGE_KEY) === 'true';
    } catch {
      // Storage may be unavailable in restricted browser contexts.
      return false;
    }
  }

  setDebugLogging(enabled: boolean): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      if (enabled) {
        localStorage.setItem(LOOKUP_SYNC_DEBUG_STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(LOOKUP_SYNC_DEBUG_STORAGE_KEY);
      }
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }

  /** Human-readable module name for a context key; the shell-wide bucket has no module of its own. */
  private toModuleLabel(contextKey: string): string {
    return contextKey === COMMON_CONTEXT_KEY ? 'common' : contextKey;
  }

  private pushQueueEntry(
    contextKey: string,
    source: LookupSyncTriggerSource,
    state: LookupSyncQueueEntryState,
    note: string | null = null
  ): string {
    const now = Date.now();
    const entry: LookupSyncQueueEntry = {
      id: `${++this.queueEntrySeq}`,
      module: this.toModuleLabel(contextKey),
      source,
      state,
      requestId: null,
      queuedAt: now,
      startedAt: state === 'running' ? now : null,
      endedAt: state === 'skipped' ? now : null,
      note,
    };

    this.syncQueueSignal.update((entries) => [...entries, entry].slice(-LOOKUP_SYNC_QUEUE_HISTORY_LIMIT));
    return entry.id;
  }

  private updateQueueEntryById(
    entryId: string | null,
    changes: Partial<Omit<LookupSyncQueueEntry, 'id' | 'module' | 'queuedAt'>>
  ): void {
    if (!entryId) {
      return;
    }

    this.syncQueueSignal.update((entries) =>
      entries.map((entry) => (entry.id === entryId ? { ...entry, ...changes } : entry))
    );
  }

  private updateRunQueueEntry(
    contextKey: string,
    source: LookupSyncTriggerSource,
    changes: Partial<Omit<LookupSyncQueueEntry, 'id' | 'module' | 'queuedAt'>>
  ): void {
    if (this.pendingRunQueueEntryId) {
      this.updateQueueEntryById(this.pendingRunQueueEntryId, changes);
      return;
    }

    this.updateQueueEntry(contextKey, source, changes);
  }

  /** Debug-only: a navigation that produced no sync is otherwise completely silent. */
  private recordSkippedRequest(
    contextKey: string,
    source: LookupSyncTriggerSource,
    note: string,
    queueEntryId: string | null = null
  ): void {
    this.logDebug('Sync not started', { contextKey, source, note });

    if (queueEntryId) {
      this.updateQueueEntryById(queueEntryId, {
        state: 'skipped',
        endedAt: Date.now(),
        note,
      });
      return;
    }

    if (this.isDebugLoggingEnabled()) {
      this.pushQueueEntry(contextKey, source, 'skipped', note);
    }
  }

  /** Promotes the newest queued/running entry for the module, or creates one when the run never queued. */
  private updateQueueEntry(
    contextKey: string,
    source: LookupSyncTriggerSource,
    changes: Partial<Omit<LookupSyncQueueEntry, 'id' | 'module' | 'queuedAt'>>
  ): void {
    const module = this.toModuleLabel(contextKey);

    this.syncQueueSignal.update((entries) => {
      // A same-module "queued" entry from a later, still-waiting navigation must not be mistaken
      // for the one actually executing, so prefer a running match before falling back to queued.
      let target: LookupSyncQueueEntry | undefined;
      for (let i = entries.length - 1; i >= 0; i--) {
        const candidate = entries[i];
        if (candidate && candidate.module === module && candidate.state === 'running') {
          target = candidate;
          break;
        }
      }

      if (!target) {
        for (let i = entries.length - 1; i >= 0; i--) {
          const candidate = entries[i];
          if (candidate && candidate.module === module && candidate.state === 'queued') {
            target = candidate;
            break;
          }
        }
      }

      if (!target) {
        const now = Date.now();
        const entry: LookupSyncQueueEntry = {
          id: `${++this.queueEntrySeq}`,
          module,
          source,
          state: 'running',
          requestId: null,
          queuedAt: now,
          startedAt: now,
          endedAt: null,
          note: null,
          ...changes,
        };

        return [...entries, entry].slice(-LOOKUP_SYNC_QUEUE_HISTORY_LIMIT);
      }

      const updated: LookupSyncQueueEntry = { ...target, ...changes };
      return entries.map((entry) => (entry.id === updated.id ? updated : entry));
    });
  }

  private finishQueueEntry(contextKey: string, state: 'completed' | 'failed', note: string | null = null): void {
    this.updateRunQueueEntry(contextKey, this.pendingSource ?? 'manual', {
      state,
      endedAt: Date.now(),
      note,
    });
  }

  private createRequestId(source: string, contextKey: string): string {
    return ['lookup-sync', source, contextKey, String(Date.now()), Math.random().toString(36).slice(2, 8)].join(
      REQUEST_ID_SEPARATOR
    );
  }

  private parseContextKeyFromRequestId(requestId: string): string {
    return requestId.split(REQUEST_ID_SEPARATOR)[2] ?? COMMON_CONTEXT_KEY;
  }

  private updateCurrentTablesLabel(): void {
    const list = Array.from(this.activeWritingTables);
    const label = list.length > 0 ? list.join(', ') : null;
    this.progress.update((p) => (p.currentTable === label ? p : { ...p, currentTable: label }));
  }

  private buildServiceWorkerAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    const session = this.authStateService?.getSession();
    const token = session?.status === AuthSessionStatus.SignedIn ? (session.customAccessToken ?? null) : null;

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const impersonation = this.impersonationService?.impersonationState();
    if (impersonation?.isActive && impersonation.impersonationTokenId) {
      headers[IMPERSONATION_SESSION_HEADER] = impersonation.impersonationTokenId;
    }

    return headers;
  }

  private canSyncForSource(source: LookupSyncTriggerSource, contextKey?: string): boolean {
    // A forced hard refresh is an explicit "resync everything now" action (e.g. after clearing
    // IndexedDB from Settings) and must run regardless of which module the caller is on.
    if (source === 'startup' || source === 'online' || this.forceRefresh) {
      return true;
    }

    const module = contextKey === COMMON_CONTEXT_KEY ? null : (contextKey ?? this.resolveCurrentModule());
    if (!module) {
      return this.getEnabledModules().length === 0;
    }

    return this.isModuleEnabled(module);
  }

  /**
   * Convenience wrapper so callers don't re-derive "current module" themselves and risk disagreeing with the
   * header/profile indicator (which uses this same active module).
   */
  getSyncStatusForActiveModule(): LookupSyncModuleStatus {
    return this.getSyncStatusForModule(this.activeModule());
  }

  getGlobalSyncStatus(): LookupSyncModuleStatus {
    return this.status();
  }

  private buildGlobalSyncStatus(): LookupSyncModuleStatus {
    const state = this.state();
    const progress = this.progress();
    const lastRun = this.lastRun();
    const isActive = state === 'checking' || state === 'syncing';
    const source = isActive || state === 'failed' ? progress : (lastRun ?? progress);
    const failedTables = source.details
      .filter((detail) => detail.status === 'failed')
      .map((detail) => detail.tableName)
      .filter(Boolean);
    const hasErrors = state === 'failed' || source.failed > 0 || Boolean(this.lastError());
    const percent =
      progress.total > 0
        ? Math.min(100, Math.round(((progress.completed + progress.failed + progress.skipped) / progress.total) * 100))
        : 0;

    const { reason, detail } = this.resolveStatusReason(this.activeContextKey, {
      state,
      progress,
      lastRun,
      error: this.lastError(),
    });

    return {
      state,
      progress,
      lastRun,
      error: this.lastError(),
      percent,
      isActive,
      hasErrors,
      failedTables,
      label: this.buildStatusLabel(state, progress, lastRun, this.lastError()),
      reason,
      reasonDetail: detail,
    };
  }

  getSyncViewForModule(moduleName: string | null | undefined): LookupSyncModuleView {
    const contextKey = this.resolveContextKeyForModule(moduleName);
    const runForContext = this.lastRunByContext()[contextKey] ?? null;
    const errorForContext = this.lastErrorByContext()[contextKey] ?? null;

    return {
      state: this.getStateForContext(contextKey, runForContext, errorForContext),
      progress: this.getProgressForContext(contextKey),
      lastRun: runForContext,
      error: errorForContext,
    };
  }

  getSyncStatusForModule(moduleName: string | null | undefined): LookupSyncModuleStatus {
    const contextKey = this.resolveContextKeyForModule(moduleName);
    const view = this.getSyncViewForModule(moduleName);
    const { state, progress, lastRun } = view;

    const isActive = state === 'checking' || state === 'syncing';
    const source = isActive || state === 'failed' ? progress : (lastRun ?? progress);
    const failedTables = source.details
      .filter((detail) => detail.status === 'failed')
      .map((detail) => detail.tableName)
      .filter(Boolean);
    const hasErrors = state === 'failed' || source.failed > 0 || Boolean(view.error);

    const percent =
      progress.total > 0
        ? Math.min(100, Math.round(((progress.completed + progress.failed + progress.skipped) / progress.total) * 100))
        : 0;

    const { reason, detail } = this.resolveStatusReason(contextKey, view);

    return {
      ...view,
      percent,
      isActive,
      hasErrors,
      failedTables,
      label: this.buildStatusLabel(state, progress, lastRun, view.error),
      reason,
      reasonDetail: detail,
    };
  }

  /**
   * The user-facing label collapses several states into one sentence ("Preparing…" covers both an in-flight precheck
   * and "nothing has ever run"), so debugging needs the underlying cause spelled out.
   */
  private resolveStatusReason(
    contextKey: string,
    view: LookupSyncModuleView
  ): { reason: LookupSyncStatusReason; detail: string } {
    const { state, progress, lastRun, error } = view;
    const module = this.toModuleLabel(contextKey);
    const owner = this.toModuleLabel(this.activeContextKey);

    if (state === 'checking') {
      if (this.queuedContextKeys().includes(contextKey)) {
        return {
          reason: 'queued-behind-run',
          detail: this.isRunning
            ? `"${module}" is queued behind the in-flight run for "${owner}".`
            : `"${module}" is queued but nothing is running — the queue has stalled.`,
        };
      }

      if (this.pendingContextKey === null || this.pendingContextKey === contextKey) {
        return {
          reason: 'checking-own-run',
          detail: `Precheck for "${module}" is running (waiting for the service worker / lookup config).`,
        };
      }

      return {
        reason: 'mirroring-other-context',
        detail: `"${owner}" is syncing and "${module}" has no result of its own yet, so it mirrors that run.`,
      };
    }

    if (state === 'syncing') {
      return {
        reason: 'syncing',
        detail: `Downloading ${progress.total} table(s) for "${owner}"; current: ${progress.currentTable ?? 'n/a'}.`,
      };
    }

    if (state === 'failed') {
      return { reason: 'failed', detail: error ?? `${progress.failed} table(s) failed for "${owner}".` };
    }

    if (state === 'completed') {
      return progress.failed > 0
        ? { reason: 'completed-with-errors', detail: `Run finished with ${progress.failed} failed table(s).` }
        : { reason: 'completed', detail: `Run finished: ${progress.completed} loaded, ${progress.skipped} cached.` };
    }

    if (error) {
      return { reason: 'idle-error', detail: `Last run for "${module}" errored: ${error}` };
    }

    if (lastRun) {
      return lastRun.failed > 0
        ? { reason: 'idle-last-run-failed', detail: `Last run for "${module}" had ${lastRun.failed} failed table(s).` }
        : {
            reason: 'idle-up-to-date',
            detail: `Last run for "${module}": ${lastRun.completed} loaded, ${lastRun.skipped} cached.`,
          };
    }

    return {
      reason: 'idle-never-run',
      detail: `No sync has completed for "${module}" yet and none is in flight — the label is the default placeholder.`,
    };
  }

  /** Plain-language status: users should not see table names or sync internals. */
  private buildStatusLabel(
    state: LookupBackgroundState,
    progress: LookupBackgroundSyncProgress,
    lastRun: LookupBackgroundSyncResult | null,
    error: string | null
  ): string {
    if (state === 'checking') {
      return 'Preparing assessment coding information';
    }

    if (state === 'syncing') {
      return 'Loading assessment coding information. This may take a few minutes.';
    }

    if (state === 'failed') {
      return 'Assessment coding information could not be loaded';
    }

    if (state === 'completed') {
      return progress.failed > 0
        ? 'Assessment coding information could not be loaded'
        : 'Assessment coding information is ready';
    }

    // Idle: a failed run leaves the error behind but never records a lastRun, so check it first.
    if (error) {
      return 'Assessment coding information could not be loaded';
    }

    if (lastRun) {
      return lastRun.failed > 0
        ? 'Assessment coding information could not be loaded'
        : 'Assessment coding information is up to date';
    }

    return 'Preparing assessment coding information';
  }

  private resolveAllowedTables(source: LookupSyncTriggerSource, contextKey?: string): string[] {
    const registeredTables = this.lookupService
      .getRegisteredLookupTableNames()
      .map((tableName) => normalizeLookupName(tableName));

    // Tables marked excludeFromSync (icd-codes, drug-codes, clients, vendors) never go through this
    // engine, in any trigger source — including a forced hard refresh — since they're downloaded
    // on demand elsewhere (see LOOKUP_TABLE_SCOPE_RULES comment above).
    const uniqueTables = Array.from(new Set(registeredTables)).filter(
      (tableName) => !this.getTableScopeRule(tableName).excludeFromSync
    );

    // Forced hard refresh always covers every registered table, including module-scoped ones
    // (icd/drug/loinc) that would otherwise be filtered out unless the user is on that module.
    if (this.forceRefresh) {
      return uniqueTables;
    }

    // A queued run keeps its own module scope; by the time it drains the user may be elsewhere.
    const currentModule =
      contextKey === undefined ? this.resolveCurrentModule() : contextKey === COMMON_CONTEXT_KEY ? null : contextKey;
    const isStartupLike = source === 'startup' || source === 'online';

    // "Load full data on start" means every registered table, regardless of the module the user landed on.
    if (isStartupLike && this.getLoadFullDataOnStart()) {
      return uniqueTables;
    }

    return uniqueTables.filter((tableName) => {
      const rule = this.getTableScopeRule(tableName);
      const modules = this.normalizeModules(rule.modules);

      if (isStartupLike) {
        // Shell-preload tables always load on startup; a startup scoped to a known module (see
        // resolveContextKeyForSource) also needs that module's own tables in the same request.
        if (rule.preloadOnShellInit !== false) {
          return true;
        }
        return currentModule != null && modules.includes(currentModule);
      }

      if (modules.length === 0) {
        return true;
      }

      if (!currentModule) {
        return false;
      }

      return modules.includes(currentModule);
    });
  }

  /** Tables every module depends on, i.e. the shell-preload set. */
  private coversCommonTables(allowedTables: string[]): boolean {
    const covered = new Set(allowedTables.map((tableName) => normalizeLookupName(tableName)));

    return this.lookupService
      .getRegisteredLookupTableNames()
      .map((tableName) => normalizeLookupName(tableName))
      .filter((tableName) => !this.getTableScopeRule(tableName).excludeFromSync)
      .filter((tableName) => this.getTableScopeRule(tableName).preloadOnShellInit !== false)
      .every((tableName) => covered.has(tableName));
  }

  private resolveContextKeyForSource(source: LookupSyncTriggerSource): string {
    if (source === 'online' || this.forceRefresh) {
      return COMMON_CONTEXT_KEY;
    }

    const currentModule = this.resolveCurrentModule();
    if (!currentModule || !this.isModuleEnabled(currentModule)) {
      return COMMON_CONTEXT_KEY;
    }

    // Startup uses the module the user actually landed on (read straight from the URL when no
    // navigation has fired yet) so the first sync loads its tables too, instead of common-only
    // followed by a second, queued module-scoped run.
    return currentModule;
  }

  private resolveContextKeyForModule(moduleName: string | null | undefined): string {
    const normalized = normalizeModuleName(moduleName);
    if (!normalized || !this.isModuleEnabled(normalized)) {
      return COMMON_CONTEXT_KEY;
    }

    return normalized;
  }

  private getStateForContext(
    contextKey: string,
    runForContext: LookupBackgroundSyncResult | null,
    errorForContext: string | null
  ): LookupBackgroundState {
    const globalState = this.state();

    // This context owns the current run, so its live state wins.
    if (globalState !== 'idle' && this.pendingContextKey === contextKey) {
      return globalState;
    }

    // Queued behind an in-flight run: show progress, not a stale/failed state.
    if (this.queuedContextKeys().includes(contextKey)) {
      return 'checking';
    }

    // Another context is syncing and we have nothing of our own to report yet.
    if (!runForContext && !errorForContext && (globalState === 'checking' || globalState === 'syncing')) {
      return globalState;
    }

    // Past results live on in lastRun/error; the state itself settles back to idle.
    return 'idle';
  }

  private getProgressForContext(contextKey: string): LookupBackgroundSyncProgress {
    const globalState = this.state();
    // If sync is actively running for this context, return current progress
    if ((globalState === 'checking' || globalState === 'syncing') && this.pendingContextKey === contextKey) {
      return this.progress();
    }

    // If this context has a completed run, return its progress
    const runForContext = this.lastRunByContext()[contextKey];
    if (runForContext && !this.queuedContextKeys().includes(contextKey)) {
      const { startedAt: _startedAt, endedAt: _endedAt, ...progress } = runForContext;
      return progress;
    }

    // Fallback: If a sync is running for ANY context and this context has no progress,
    // show the global progress (e.g., startup sync visible in assessment dashboard)
    if (globalState === 'checking' || globalState === 'syncing' || this.queuedContextKeys().includes(contextKey)) {
      return this.progress();
    }

    return {
      total: 0,
      completed: 0,
      failed: 0,
      skipped: 0,
      currentTable: null,
      details: [],
    };
  }

  private updateContextResult(contextKey: string, result: LookupBackgroundSyncResult): void {
    this.lastRunByContext.update((state) => ({
      ...state,
      [contextKey]: result,
    }));
  }

  private updateContextError(contextKey: string, message: string | null): void {
    this.lastErrorByContext.update((state) => ({
      ...state,
      [contextKey]: message,
    }));
  }

  private getTableScopeRule(tableName: string): LookupTableScopeRule {
    return LOOKUP_TABLE_SCOPE_RULES[tableName] ?? DEFAULT_TABLE_SCOPE_RULE;
  }

  private normalizeModules(modules: string[] | undefined): string[] {
    if (!Array.isArray(modules)) {
      return [];
    }

    return modules
      .map((moduleName) => normalizeModuleName(moduleName))
      .filter((moduleName): moduleName is string => Boolean(moduleName));
  }

  private resolveCurrentModule(): string | null {
    if (this.activeModule()) {
      return this.activeModule();
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const path = String(window.location?.pathname ?? '');
    const segment = path.split('/').filter(Boolean)[0] ?? null;
    return normalizeModuleName(segment);
  }

  private isModuleEnabled(moduleName: string): boolean {
    const enabledModules = this.getEnabledModules();
    if (enabledModules.length === 0) {
      return true;
    }

    return enabledModules.includes(moduleName);
  }

  private getEnabledModules(): string[] {
    const configuredModules = this.config?.enabledModules;
    const modules = Array.isArray(configuredModules) ? configuredModules : DEFAULT_ENABLED_MODULES;

    return modules
      .map((moduleName) => normalizeModuleName(moduleName))
      .filter((moduleName): moduleName is string => Boolean(moduleName));
  }

  private getIsEnabled(): boolean {
    return this.config?.enabled === true;
  }

  private shouldUseServiceWorker(): boolean {
    return this.config?.useServiceWorker !== false;
  }

  isEnabled(): boolean {
    return this.getIsEnabled();
  }

  private getMinIntervalMs(): number {
    const interval = Number(this.config?.minIntervalMs);
    return Number.isFinite(interval) && interval > 0 ? Math.floor(interval) : DEFAULT_BACKGROUND_SYNC_MIN_INTERVAL_MS;
  }

  private getRequestTimeoutMs(): number {
    const timeoutMs = Number(this.config?.requestTimeoutMs);
    return Number.isFinite(timeoutMs) && timeoutMs > 0 ? Math.floor(timeoutMs) : DEFAULT_REQUEST_TIMEOUT_MS;
  }

  private getTableFetchConcurrency(): number {
    const fetchConcurrency = Number(this.config?.tableFetchConcurrency);
    return Number.isFinite(fetchConcurrency) && fetchConcurrency > 0
      ? Math.floor(fetchConcurrency)
      : DEFAULT_TABLE_FETCH_CONCURRENCY;
  }

  private getAutoStartOnStartup(): boolean {
    return this.config?.autoStartOnStartup !== false;
  }

  private getLoadFullDataOnStart(): boolean {
    return this.config?.loadFullDataOnStart === true;
  }

  private getFullDataMaxAgeDays(): number {
    const maxAgeDays = Number(this.config?.fullDataMaxAgeDays);
    return Number.isFinite(maxAgeDays) && maxAgeDays >= 0 ? maxAgeDays : DEFAULT_FULL_DATA_MAX_AGE_DAYS;
  }

  private getAutoStartOnOnline(): boolean {
    return this.config?.autoStartOnOnline !== false;
  }

  private getAutoStartOnVisible(): boolean {
    return this.config?.autoStartOnVisible !== false;
  }
}

function normalizeModuleName(moduleName: string | null | undefined): string | null {
  if (!moduleName) {
    return null;
  }

  const normalized = String(moduleName).trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function normalizeLookupName(name: string | null | undefined): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}
